import React, { useState, useEffect, useMemo } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TextInput,
    TouchableOpacity
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import Data from './Data';
import { getStorage, setStorage } from '../storage';
import { useScale, ScaleFn , useDarkMode} from './common';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};
const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

export default function PushAlram({ navigation }: any) {
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [title, setTitle] = useState('');
    const [contents, setContents] = useState('');
    const { dark, setDark } = useDarkMode();
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!isFocused) return;
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    const onPush = () => {
        if (!title.trim()) { alert('제목을 입력하세요.'); return; }
        if (!contents.trim()) { alert('내용을 입력하세요.'); return; }
        if (sending) return;
        setSending(true);
        Data.fetchData('notice/insertNotice', {
            TITLE: title,
            CONTENT: contents
        }, (result: any, count: any) => {
            setSending(false);
            if (count === 1) {
                setTitle('');
                setContents('');
                alert('푸시알림이 전송되었습니다.');
            }
        }, true);
    };

    return (
        <View style={[styles.container, { backgroundColor: T.bg }]}>
            <View style={[styles.cardWrap, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                {/* ─── 헤더 ─── */}
                <View style={styles.TopBox}>
                    <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>푸시 알림 전송</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <TouchableOpacity
                            style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                            onPress={() => { navigation.navigate('Setting') }}
                        >
                            <Ionicons name="settings-sharp" size={s(16)} color={T.textSub} />
                        </TouchableOpacity>
                    </View>
                </View>

                {/* ─── 제목 ─── */}
                <Text style={[styles.label, { color: T.text }]}>제목</Text>
                <View style={[styles.inputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                    <TextInput
                        autoCapitalize='none'
                        clearButtonMode='always'
                        placeholder='제목을 입력하세요.'
                        placeholderTextColor={T.muted}
                        style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(14), height: '100%', outlineStyle: 'none' }}
                        onChangeText={(text: any) => { setTitle(text) }}
                        value={title}
                    />
                </View>

                {/* ─── 내용 ─── */}
                <Text style={[styles.label, { color: T.text }]}>내용</Text>
                <View style={[styles.inputBox, styles.contentInputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                    <TextInput
                        autoCapitalize='none'
                        clearButtonMode='always'
                        placeholder='내용을 입력하세요.'
                        placeholderTextColor={T.muted}
                        multiline={true}
                        style={{ fontSize: s(14), color: T.text, flex: 1, padding: s(14), height: '100%', outlineStyle: 'none', textAlignVertical: 'top' }}
                        onChangeText={(text: any) => { setContents(text) }}
                        value={contents}
                    />
                </View>

                {/* ─── 전송 버튼 ─── */}
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: s(20) }}>
                    <TouchableOpacity style={[styles.sendBtn, { opacity: sending ? 0.5 : 1 }]} onPress={() => { onPush() }} disabled={sending}>
                        <Ionicons name="send" size={s(14)} color="#fff" style={{ marginRight: s(6) }} />
                        <Text style={{ fontSize: s(14), fontWeight: '700', color: '#fff' }}>{sending ? '전송 중...' : '전송'}</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </View>
    );
};

const makeStyles = (s: ScaleFn) => StyleSheet.create({
    container: {
        flex: 1,
        padding: s(24),
    },
    cardWrap: {
        borderRadius: s(16),
        borderWidth: 0.5,
        overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: s(20),
        elevation: 3,
        paddingTop: s(20),
        paddingHorizontal: s(24),
        paddingBottom: s(24),
    },
    TopBox: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: s(16),
    },
    iconBtn: {
        width: s(34),
        height: s(34),
        borderRadius: s(10),
        justifyContent: 'center',
        alignItems: 'center',
    },
    label: {
        fontSize: s(14),
        fontWeight: '600',
        marginBottom: s(8),
    },
    inputBox: {
        borderWidth: 1,
        borderRadius: s(10),
        height: s(42),
        marginBottom: s(18),
    },
    contentInputBox: {
        height: s(280),
    },
    sendBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#304156',
        borderRadius: s(10),
        paddingHorizontal: s(20),
        height: s(38),
    },
});
